import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Modal from "../Core/Modal/Modal";
import AppText from "../Core/AppText/AppText";
import Button from "../Core/Button/Button";
import Flex from "../Layout/Flex";
import LoadingIndicator from "../Shared/LoadingIndicator/LoadingIndicator";
import { useConsultantsByCollectiveAgreement } from "../../networking/guaranteedHours.hooks";

export interface UpdateCollectiveAgreementModalProps {
	collectiveAgreementId: string;
	onConfirm: () => void;
	onCancel: () => void;
}

/**
 * Modal informing admin how many consultants will be affected when updating
 * a collective agreement's guaranteed hours or salary.
 *
 * The backend automatically propagates changes to all consultants using this agreement.
 * This modal just shows the user the impact before they confirm.
 */
const UpdateCollectiveAgreementModal: React.FC<UpdateCollectiveAgreementModalProps> = ({
	collectiveAgreementId,
	onConfirm,
	onCancel,
}) => {
	const { t } = useTranslation();

	// Query consultant count
	const { data: consultantsData, isLoading: isLoadingConsultants } =
		useConsultantsByCollectiveAgreement(collectiveAgreementId);

	const consultantCount = consultantsData?.count || 0;

	if (isLoadingConsultants) {
		return (
			<Modal maxWidth="550px" padding="3.125rem 4rem">
				<Flex direction="column" alignItems="center">
					<LoadingIndicator />
				</Flex>
			</Modal>
		);
	}

	return (
		<Modal maxWidth="550px" padding="3.125rem 4rem" onClose={onCancel}>
			<AppText size="xm" weight="bold" margin="0 0 1.5rem 0">
				{t("collectiveAgreement.updateGuaranteedHours.title")}
			</AppText>

			<Flex direction="column" alignItems="flex-start" margin="0 0 2rem 0">
				<AppText size="m" margin="0 0 1rem 0">
					{t("collectiveAgreement.updateGuaranteedHours.usedByConsultants", {
						count: consultantCount,
					})
						.replace(`${consultantCount}`, `**${consultantCount}**`)
						.split("**")
						.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part))}
				</AppText>
				<AppText size="m">{t("collectiveAgreement.updateGuaranteedHours.autoUpdate")}</AppText>
			</Flex>

			<ModalButtonWrapper>
				<Button onClick={onConfirm} width="100%" margin="0 0 0.75rem 0" fontSize="s" height="40px">
					{t("collectiveAgreement.updateGuaranteedHours.updateAgreement")}
				</Button>
				<CancelButton onClick={onCancel}>{t("CANCEL")}</CancelButton>
			</ModalButtonWrapper>
		</Modal>
	);
};

export default UpdateCollectiveAgreementModal;

const ModalButtonWrapper = styled.div`
	display: flex;
	align-items: center;
	flex-direction: column;
	width: 100%;
`;

const CancelButton = styled.button`
	margin-top: 1rem;
	background: none;
	border: none;
	color: ${(props) => props.theme.colors.grayText};
	font-size: 0.875rem;
	cursor: pointer;
	padding: 0.5rem;

	&:hover {
		text-decoration: underline;
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}
`;
